import React from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import { AppUI, ExerciseUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import { noop } from 'lodash';
import Description from 'components/exercise/description';
import ExerciseHighlight from 'components/exercise/exercise_highlight';
import ExerciseDrag from 'components/exercise/exercise_drag';
import ExerciseWordEnter from 'components/exercise/exercise_word_enter';
import ExerciseWordOptions from 'components/exercise/exercise_word_options';
import ExerciseMatchMedia from 'components/exercise/exercise_match_media';
import ExerciseMatchText from 'components/exercise/exercise_match_text';
import ExerciseMultipleChoiceMedia from 'components/exercise/exercise_multiple_choice_media';
import ExerciseReorder from 'components/exercise/exercise_reorder';
import ExerciseSentenceEnter from 'components/exercise/exercise_sentence_enter';
import ExerciseTags from 'components/exercise/exercise_tags';
import ExpandableDescription from 'components/exercise/expandable_description';
import Spinner from 'components/spinner';
import Text from 'components/text';
import ExerciseButtons from 'components/worksheet/exercise_buttons';
import YoutubeVideo from 'components/youtube_video';
import TextDisplay from 'components/text_display';
import Stack, { gaps } from 'components/layout/stack';
import { Theme } from '@seedlang/constants';
import ReactTooltip from 'react-tooltip';
import cx from 'classnames';

const COMPONENTS = {
  matchText: ExerciseMatchText,
  reorder: ExerciseReorder,
  wordEnter: ExerciseWordEnter,
  drag: ExerciseDrag,
  wordOptions: ExerciseWordOptions,
  matchMedia: ExerciseMatchMedia,
  sentenceEnter: ExerciseSentenceEnter,
  highlight: ExerciseHighlight,
  multipleChoice: ExerciseMultipleChoiceMedia,
  simpleTextPrompt: () => null,
};

const slugsWithEnglishPrompts = ['drag', 'reorder'];

const TitleWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  margin: 0 0 10px 0;
  align-items: center;
  gap: ${gaps.sm};
`;

const EyeToggle = styled.button`
  padding: 0;
  font-size: 18px;
  color: ${Theme.darkGray};
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s ease;
  display: flex;

  &:hover {
    color: black;
  }
`;

const VideoWrapper = styled.div`
  width: 100%;
  max-width: 540px;
  aspect-ratio: 16 / 9;
  margin: ${gaps.md} auto;
`;

function ExerciseContent({
  showYoutubeVideo = true,
  showExerciseButtons = true,
  showRatingComment = false,
  ratingCommentSubmitted = false,
  comment = '',
  toggleEnglishPrompts = noop,
  goToNextExercise = noop,
  goBackToTop = noop,
  onSubmitRating = noop,
  onSubmitRatingComment = noop,
  setComment = noop,
}) {
  return (
    <>
      <TitleWrapper>
        <Text heading={3}>{ExerciseUI.exercise.name}</Text>
        <Stack inline gap='sm' alignItems='center'>
          <ExerciseTags exercise={ExerciseUI.exercise} />
          {
            slugsWithEnglishPrompts.includes(ExerciseUI.exercise?.exerciseType?.slug) &&
              <EyeToggle
                onClick={toggleEnglishPrompts}
                data-tip
                data-for='english-prompts'
              >
                <i
                  className={cx('fa', {
                    'fa-eye-slash': ExerciseUI.user.hideEnglishPrompts,
                    'fa-eye': !ExerciseUI.user.hideEnglishPrompts,
                  })}
                />
                <ReactTooltip id='english-prompts' place='top'>
                  {ExerciseUI.user.hideEnglishPrompts ? 'Show English Prompts' : 'Hide English Prompts'}
                </ReactTooltip>
              </EyeToggle>
          }
        </Stack>
      </TitleWrapper>
      {
        ExerciseUI.exercise.description && ExerciseUI.exercise.autoExpandDescription &&
          <Description>
            <TextDisplay
              htmlText={ExerciseUI.exercise.descriptionFormatted}
            />
          </Description>
      }
      {
        ExerciseUI.exercise.defaultDescription &&
          <ExpandableDescription
            exerciseId={ExerciseUI.exercise?.id}
            defaultDescription={ExerciseUI.exercise.defaultDescriptionFormatted}
            expandedDescription={ExerciseUI.exercise.expandedDescriptionFormatted}
            blockExpansion={!ExerciseUI.worksheet?.accessStatus?.hasExerciseAccess}
          />
      }
      {
        showYoutubeVideo && ExerciseUI.exercise.formattedDescriptionVideo &&
          <VideoWrapper>
            <YoutubeVideo
              url={ExerciseUI.exercise.formattedDescriptionVideo}
              show
              allowFloating
            />
          </VideoWrapper>
      }
      {
        !ExerciseUI.exercise?.enriched && ExerciseUI.exercise?.exerciseEntriesCount > 0 &&
          <Spinner
            background={AppUI.site.accentColor || '#868686'}
          />
      }
      {
        ExerciseUI.exercise?.exerciseType?.slug &&
          React.createElement(COMPONENTS[ExerciseUI.exercise.exerciseType.slug],
            {
              exerciseId: ExerciseUI.exercise.id,
              isMobile: ExerciseUI.layout.isMobile,
              hideEnglishPrompts: ExerciseUI.user.hideEnglishPrompts,
              inDeck: false,
            },
          )
      }
      {
        showExerciseButtons && ExerciseUI.worksheet?.accessStatus?.hasExerciseAccess && isPresent(ExerciseUI.exercise) && isPresent(ExerciseUI.exercise.exerciseType) && isPresent(ExerciseUI.exercise.exerciseType.slug) &&
          <ExerciseButtons
            exercisesAreBlocked={!ExerciseUI.worksheet?.accessStatus?.hasExerciseAccess}
            goToNextExercise={goToNextExercise}
            showRatingComment={showRatingComment}
            goBackToTop={goBackToTop}
            onSubmitRating={onSubmitRating}
            onSubmitRatingComment={onSubmitRatingComment}
            setComment={setComment}
            comment={comment}
            ratingCommentSubmitted={ratingCommentSubmitted}
          />
      }
    </>
  );
}

export default observer(ExerciseContent);
